import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

interface PatchableNavigator {
  push: (to: unknown, state?: unknown, opts?: unknown) => void;
  replace: (to: unknown, state?: unknown, opts?: unknown) => void;
  [key: string]: unknown;
}

interface PendingNavigation {
  to: unknown;
  state?: unknown;
  opts?: unknown;
  method: 'push' | 'replace';
}

export function useCallbackPrompt(when: boolean): [boolean, () => void, () => void] {
  const { navigator } = useContext(NavigationContext) as unknown as { navigator: PatchableNavigator };
  const [showPrompt, setShowPrompt] = useState(false);
  const pendingNavigation = useRef<PendingNavigation | null>(null);
  const originalPush = useRef<PatchableNavigator['push'] | null>(null);
  const originalReplace = useRef<PatchableNavigator['replace'] | null>(null);

  useEffect(() => {
    if (!when) {
      if (originalPush.current) {
        navigator.push = originalPush.current;
        originalPush.current = null;
      }
      if (originalReplace.current) {
        navigator.replace = originalReplace.current;
        originalReplace.current = null;
      }
      setShowPrompt(false);
      pendingNavigation.current = null;
      return undefined;
    }

    originalPush.current ??= navigator.push.bind(navigator);
    originalReplace.current ??= navigator.replace.bind(navigator);

    navigator.push = (to: unknown, state?: unknown, opts?: unknown) => {
      pendingNavigation.current = { to, state, opts, method: 'push' };
      setShowPrompt(true);
    };

    navigator.replace = (to: unknown, state?: unknown, opts?: unknown) => {
      pendingNavigation.current = { to, state, opts, method: 'replace' };
      setShowPrompt(true);
    };

    return () => {
      if (originalPush.current) {
        navigator.push = originalPush.current;
        originalPush.current = null;
      }
      if (originalReplace.current) {
        navigator.replace = originalReplace.current;
        originalReplace.current = null;
      }
    };
  }, [when, navigator]);

  const cancelRouteNavigation = useCallback(() => {
    setShowPrompt(false);
    pendingNavigation.current = null;
  }, []);

  const confirmRouteNavigation = useCallback(() => {
    setShowPrompt(false);
    const pending = pendingNavigation.current;
    pendingNavigation.current = null;
    if (!pending) return;

    // Restore originals before executing so the real navigation goes through
    if (originalPush.current) {
      navigator.push = originalPush.current;
      originalPush.current = null;
    }
    if (originalReplace.current) {
      navigator.replace = originalReplace.current;
      originalReplace.current = null;
    }

    if (pending.method === 'push') {
      navigator.push(pending.to, pending.state, pending.opts);
    } else {
      navigator.replace(pending.to, pending.state, pending.opts);
    }
  }, [navigator]);

  return [showPrompt, confirmRouteNavigation, cancelRouteNavigation];
}
